import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { apiService } from "../../utils/api";
import { FaFileAlt, FaSearch, FaEye, FaArrowLeft } from "react-icons/fa";
import "./TeacherStyles.css";

function StudentDocumentList() {
  const [documents, setDocuments] = useState([]);
  const [imageDocuments, setImageDocuments] = useState([]);
  const [audioDocuments, setAudioDocuments] = useState([]);
  const [student, setStudent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [sortOrder, setSortOrder] = useState("desc");
  const [filterType, setFilterType] = useState("all");
  const { id } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    fetchStudentInfo();
    fetchDocuments();
  }, [id, sortOrder, filterType]);

  const fetchStudentInfo = async () => {
    try {
      console.log("Fetching student info for ID:", id);
      const response = await apiService.getStudent(id);
      if (response && response.data && response.data.success) {
        setStudent(response.data.student);
      } else {
        setError("Failed to load student information");
      }
    } catch (err) {
      console.error("Student info fetch error:", err);
      setError("Error fetching student information: " + (err.response?.data?.message || err.message || "Unknown error"));
    }
  };

  const fetchDocuments = async () => {
    setLoading(true);
    try {
      // We need to add this endpoint to the API service
      const response = await apiService.getStudentDocuments(id);

      if (response && response.data && response.data.success) {
        setDocuments(response.data.documents || []);
      } else {
        console.error("API response unsuccessful:", response);
        setError("Failed to load documents");
      }

      const imageResponse = await apiService.getStudentImageDocuments(id);
      if (imageResponse && imageResponse.data && imageResponse.data.success) {
        setImageDocuments(imageResponse.data.documents || []);
      } else {
        console.error("API response unsuccessful for image documents:", imageResponse);
        setError("Failed to load image documents");
      }

      const audioResponse = await apiService.getStudentAudioDocuments(id);
      if (audioResponse && audioResponse.data && audioResponse.data.success) {
        setAudioDocuments(audioResponse.data.documents || []);
      } else {
        console.error("API response unsuccessful for audio documents:", audioResponse);
        setError("Failed to load audio documents");
      }
    } catch (err) {
      console.error("Documents fetch error:", err);
      setError("Error fetching documents: " + (err.response?.data?.message || err.message || "Unknown error"));
    } finally {
      setLoading(false);
    }
  };

  const handleViewDocument = (docId) => {
    navigate(`/dashboard/teacher/students/${id}/documents/${docId}`);
  };

  const handleViewImageDocument = (docId) => {
    navigate(`/dashboard/teacher/students/${id}/documents-image/${docId}`);
  };

  const handleViewAudioDocument = (docId) => {
    navigate(`/dashboard/teacher/students/${id}/documents-audio/${docId}`);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const filteredDocuments = documents
    .filter((doc) => {
      if (filterType === "all") return true;
      return doc.documentType === filterType;
    })
    .filter((doc) => {
      return doc.title.toLowerCase().includes(searchTerm.toLowerCase());
    })
    .sort((a, b) => {
      if (sortOrder === "asc") {
        return new Date(a.createdAt) - new Date(b.createdAt);
      } else {
        return new Date(b.createdAt) - new Date(a.createdAt);
      }
    });

  const goBack = () => {
    navigate(-1);
  };

  if (loading && !documents.length) {
    return (
      <div className="documents-container">
        <div className="loading-spinner">Loading...</div>
      </div>
    );
  }

  if (error && !documents.length) {
    return (
      <div className="documents-container">
        <div className="error-message">{error}</div>
        <button className="btn btn-primary" onClick={fetchDocuments}>
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="documents-container">
      <div className="section-header">
        <button className="back-button" onClick={goBack}>
          <FaArrowLeft /> Back
        </button>
        <h2>
          <FaFileAlt /> {student ? `${student.username}'s Documents` : "Student Documents"}
        </h2>
      </div>

      {/* <div className="controls-container">
        <div className="search-bar">
          <FaSearch />
          <input type="text" placeholder="Search documents..." value={searchTerm} onChange={handleSearchChange} />
        </div>
      </div> */}

      <h3>Text Documents</h3>

      {filteredDocuments.length === 0 ? (
        <div className="no-documents">
          <p>No documents found.</p>
        </div>
      ) : (
        <div className="documents-grid">
          {filteredDocuments.map((document) => (
            <div key={document.id} className="document-card">
              <div className="document-icon">
                <FaFileAlt />
              </div>
              <div className="document-info">
                <h3>{document.title}</h3>
                <p className="document-date">{new Date(document.createdAt).toLocaleDateString()}</p>
                {document.comparisonScore && <p className="document-score">Score: {document.comparisonScore.toFixed(2)}%</p>}
                {document.typingSpeed && <p className="document-speed">Speed: {document.typingSpeed} WPM</p>}
              </div>
              <div className="document-actions">
                <button className="icon-button" onClick={() => handleViewDocument(document.id)} title="View Document">
                  <FaEye />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <h3>Image Documents</h3>

      {imageDocuments.length > 0 && (
        <div className="documents-grid">
          {imageDocuments.map((document) => (
            <div key={document.id} className="document-card">
              <div className="document-icon">
                <FaFileAlt />
              </div>
              <div className="document-info">
                <h3>{document.title}</h3>
                <p className="document-date">{new Date(document.createdAt).toLocaleDateString()}</p>
                {document.comparisonScore && <p className="document-score">Score: {document.comparisonScore.toFixed(2)}%</p>}
                {document.typingSpeed && <p className="document-speed">Speed: {document.typingSpeed} WPM</p>}
              </div>
              <div className="document-actions">
                <button className="icon-button" onClick={() => handleViewImageDocument(document.id)} title="View Document">
                  <FaEye />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <h3>Audio Documents</h3>

      {audioDocuments.length > 0 && (
        <div className="documents-grid">
          {audioDocuments.map((document) => (
            <div key={document.id} className="document-card">
              <div className="document-icon">
                <FaFileAlt />
              </div>
              <div className="document-info">
                <h3>{document.title}</h3>
                <p className="document-date">{new Date(document.createdAt).toLocaleDateString()}</p>
                {document.comparisonScore && <p className="document-score">Score: {document.comparisonScore.toFixed(2)}%</p>}
                {document.typingSpeed && <p className="document-speed">Speed: {document.typingSpeed} WPM</p>}
              </div>
              <div className="document-actions">
                <button className="icon-button" onClick={() => handleViewAudioDocument(document.id)} title="View Document">
                  <FaEye />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default StudentDocumentList;
